// #region Imports
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CancelIcon from "@mui/icons-material/Cancel";
import LogoutIcon from "@mui/icons-material/Logout";
import EditSquareIcon from "@mui/icons-material/EditSquare";
import MessageIcon from "@mui/icons-material/Message";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import { styled, Theme, ThemeProvider } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { hugsItsLabel } from "_shared/_views/buttons/action-button-width";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { getForumsByTopic, populateForumsByTopic } from "messaging/forums/_stores/forums-redux-slice";
import Topic from "messaging/forums/topics/topic";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { MaskedTeam } from "work/masked-fields";
import { Content, Header, TabContent, TabsContainer } from "work/_views/work-page-styled-components";
import {
  paneFlushContent,
  paneIsCompact,
  workPaneContainment,
  workPaneGutters,
  workPaneHeaderGutters,
} from "work/_views/work-pane-layout";
import useWorkPaneSize, { WorkPaneWidthContext } from "work/_views/use-work-pane-size";
import useWorkPaneTheme from "work/_views/work-pane-typography";
import {
  useEndWorkAgreementMutation,
  useGetWorkAgreementByIdQuery,
  useQuitWorkTeamMutation,
  useReviseWorkAgreementMutation,
} from "work/work-agreements/_api/work-agreement-api";
import { getWorkAgreementById, isWorkAgreementLoadIssue } from "work/work-agreements/_store/work-agreement-redux-slice";
import { PageTab } from "work/work-agreements/_views/view/view-work-agreement-page-tabs";
import ConflictsTab from "work/work-agreements/_views/view/tabs/view-work-conflicts-tab";
import DetailsTab from "work/work-agreements/_views/view/tabs/view-work-details-tab";
import DiscountTab from "work/work-agreements/_views/view/tabs/view-work-discount-tab";
import FeeScheduleTab from "work/work-agreements/_views/view/tabs/view-work-fee-schedule-tab";
import PoliciesTab from "work/work-agreements/_views/view/tabs/view-work-policies-tab";
import TeamTab from "work/work-agreements/_views/view/tabs/view-work-team-tab";
import ClientTeamTab from "work/work-agreements/_views/view/tabs/view-work-client-team-tab";
import WorkAgreement, { WorkAgreementFieldCategory } from "work/work-agreements/work-agreement";
import ChatDrawer from "work/work-agreements/_views/chat-drawer";
import Tooltip from "@mui/material/Tooltip";
import LoadingButton from "_shared/_views/loading/loading-button";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import useMediaQuery from "@mui/material/useMediaQuery";
// #endregion


// #region Styled Components
const PageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
}));
/**
 * No horizontal padding: the sticky header around it stands in the pane's header gutter for every
 * row it holds — see `PaneBand`. A second inset here put the title 24px further in than the tab
 * strip below it, and its own compact step was the viewport's answer to a question about the pane.
 */
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  gap: theme.spacing(1),
  justifyContent: "space-between",
}));
const TitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flex: 1,
  flexDirection: "row",
  minWidth: 0,
}));
/**
 * The agreement's name, which is as long as whoever created it made it.
 *
 * Ellipsised rather than wrapped: the row it shares with the Messages button is the page's first
 * line, and a name that wraps pushes the tab strip down the page. The compact size is a step below
 * what the pane's nested theme already gives an `h4` there — see `work-pane-typography` — because
 * the breadcrumb and the button share this row and the title is the one thing on it that can yield.
 */
const PageTitle = styled(Typography)(({ theme }) => ({
  minWidth: 0,
  overflow: "hidden",
  padding: theme.spacing(0, 1),
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  [paneIsCompact(theme)]: {
    fontSize: "1.25rem",
    paddingLeft: theme.spacing(0.5),
  },
}));
const PageTitleBreadcrumb = styled("span")(({ theme }) => ({
  color: "gray",
  display: "inline-flex",
  marginRight: theme.spacing(1),
  whiteSpace: "nowrap",
}));
const StickyPageHeader = styled("section")(({ theme }) => ({
  // The page's chrome stands further off the pane's edges than the content it frames — see
  // `PaneBand`. This outranks the pane's own gutter rather than adding to it, so the title, the tab
  // strip and the actions row below all measure from one number.
  ...workPaneHeaderGutters(theme),
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  position: "sticky",
  top: 0,
  zIndex: 20,
  "&::before": {
    backgroundColor: theme.palette.background.default,
    content: '""',
    height: theme.spacing(1),
    left: 0,
    pointerEvents: "none",
    position: "absolute",
    right: 0,
    top: theme.spacing(-1),
  },
}));
const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "showMessageDrawer" && prop !== "isMessageDrawerOverlay",
})<{
  open?: boolean;
  isMessageDrawerOverlay?: boolean;
  showMessageDrawer?: boolean;
}>(({ theme, isMessageDrawerOverlay, showMessageDrawer }) => ({
  /**
   * The pane names itself here, and everything drawn inside it lays out against this element's width
   * rather than the window's — see `work-pane-layout`. Its own rules are the one exception: a
   * container cannot query itself, which is why the horizontal gutter is declared for the pane's
   * children (`workPaneGutters`) rather than written here as padding. The vertical padding below is
   * one number at every width, so it has nothing to ask.
   */
  ...workPaneContainment,
  ...workPaneGutters(theme),
  display: "flex",
  height: "100%",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  // Vertical only. The horizontal inset every row stands in is the gutter above, which knows the
  // difference between the page's chrome and a tab's content; a `paddingRight` here as well was the
  // page's own answer to the drawer being open, and the gutter is now the whole of that answer.
  paddingBottom: 0,
  paddingTop: theme.spacing(1),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: showMessageDrawer && !isMessageDrawerOverlay ? "-400px" : 0,
  /**
   * This is necessary to enable the selection of content. In the DOM, the stacking order is determined
   * by the order of appearance. Following this rule, elements appearing later in the markup will overlay
   * those that appear earlier. Since the Drawer comes after the Main content, this adjustment ensures
   * proper interaction with the underlying content.
   */
  position: "relative",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginRight: 0,
      },
    },
  ],
}));
const ButtonContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  flexDirection: "column",
}));
const MessageButton = styled(IconButton)(({ theme }) => ({
  height: '40px',
  width: '40px',
  paddingLeft: theme.spacing(1),
}));
const MessageButtonLabel = styled(Typography)(() => ({
  fontSize: "0.6em",
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));
/**
 * The row of things the reader can do to the agreement. Page chrome, so it stands in the header's
 * wider gutter alongside the title and the tab strip rather than in the content one.
 */
const ActionsContainer = styled("section")(({ theme }) => ({
  ...workPaneHeaderGutters(theme),
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  paddingBottom: theme.spacing(0),
  paddingTop: theme.spacing(0),
  width: "100%",
}));
const WorkAgreementActions = styled("section")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexWrap: "nowrap",
  },
  alignContent: "end",
  alignItems: "center",
  display: "grid",
  flex: 1,
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  width: "100%",
}));
const WorkAgreementActionSpan = styled("span")(({ theme }) => ({
  minWidth: theme.spacing(24),
  width: "100%",
}));
const WorkAgreementActionButton = styled(LoadingButton)(({ theme }) => ({
  minWidth: theme.spacing(24),
  whiteSpace: "nowrap",
  width: "100%",
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.26) !important",
    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
  },
}));
/**
 * The revision waiting on the reader, which is a state rather than a control.
 *
 * Everything in the action row below is something the reader may do and equally may not: revise the
 * agreement, end it, leave the team. This is the one thing on the page that reports a fact and asks
 * the reader to go and answer it, so it does not belong in that row at all. It sits above the row,
 * directly under the tabs, and it says in a sentence what an open revision means for the agreement
 * the reader is looking at. A control on its own cannot say that: "Review Revision" names the
 * destination and nothing else, and the reader has no way to tell whether the agreement in front of
 * them still holds.
 *
 * The sentence is the same one the Details tab carries beside the link. It is repeated rather than
 * moved, because the Revision field on that tab lists every revision relationship this agreement
 * has -- the one it replaced, the one that replaced it, and this one -- and taking one of the three
 * out of that list would make the list wrong.
 */
const ReviewRequestedBanner = styled(Alert)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  alignItems: "center",
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(3),
  marginTop: theme.spacing(1),
  // The sentence wraps; the control must not. Left alone, "Review Revision" breaks across two
  // lines against the banner's right edge and reads as two separate words.
  "& .MuiAlert-action": {
    paddingTop: 0,
  },
  "& .MuiAlert-action .MuiButton-root": {
    whiteSpace: "nowrap",
  },
}));
/**
 * An action offered rather than urged: it takes the last column, hugs its label instead of filling
 * the cell, and sits against the top of the row rather than centred in it.
 *
 * Both actions that end something wear this. Leaving the team ends the reader's own involvement and
 * is the only control a vendor team member has; ending the agreement stops the work for both
 * parties. Neither is the thing a reader came to the page to do, and a button stretched across a
 * third of the row says otherwise — which is what issue #5240 reports.
 *
 * They never appear together, so one pinned column is enough for both. `VendorTeam` rejects a team
 * whose leader is also among its members, and Leave Team asks for membership while End Agreement
 * asks for leadership.
 *
 * Once the pane is compact the row is a stacked flex column and full-width buttons are what reads
 * correctly, so the pinning is lifted there. That is a question about the pane and not the window —
 * `WorkAgreementActions` stacks on the same predicate, and a lift that asked the window instead
 * would strand the buttons pinned in a row that had already stacked.
 */
const quietWorkAgreementAction = (theme: Theme) => ({
  [paneIsCompact(theme)]: {
    alignSelf: "stretch",
    gridColumn: "auto",
    justifySelf: "stretch",
    minWidth: theme.spacing(24),
    width: "100%",
  },
  alignSelf: "start",
  gridColumn: "-2 / -1",
  justifySelf: "end",
  ...hugsItsLabel,
});
const LeaveTeamActionButton = styled(WorkAgreementActionButton)(({ theme }) => quietWorkAgreementAction(theme));
/**
 * End Agreement is wrapped for its tooltip the way the other agreement-wide actions are, so it is
 * the span that the grid places and the span that has to stop stretching. The row used to size
 * every one of its spans with a `& > span` rule; that outranks a single class however it is
 * composed, so the rule is gone and `WorkAgreementActionSpan` — which already declared the same
 * width — is what sizes the stretched ones now.
 */
const EndAgreementActionSpan = styled(WorkAgreementActionSpan)(({ theme }) => quietWorkAgreementAction(theme));
const EndAgreementActionButton = styled(WorkAgreementActionButton)(({ theme }) => ({
  [paneIsCompact(theme)]: {
    minWidth: theme.spacing(24),
    width: "100%",
  },
  ...hugsItsLabel,
}));
// #endregion

export default function ActiveWorkAgreementPage() {
  // #region Hooks
  const session = useSession();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const { id: idParam, pageTab: pageTabParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * What the page can seat is a question about the pane, not the window — the navigation drawer and
   * the chat drawer between them take 672px the window cannot see, and a docked chat is this page's
   * default. `paneIsNarrow` is the JavaScript half of `paneIsCompact`; both read the same width
   * against the same threshold, so the back control that swaps element and the rows that merely
   * restyle themselves change over together.
   */
  const { paneRef, paneWidth, isCompact: paneIsNarrow } = useWorkPaneSize();
  const paneTheme = useWorkPaneTheme(paneWidth);
  /**
   * The one thing here that really is about the window: whether there is room to seat the chat
   * beside the agreement at all, or whether it has to come over the top of it. Asking the pane would
   * be circular — the drawer is 400px of the answer.
   */
  const isMessageDrawerOverlay = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  // Work Agreement id is required
  if (!idParam) throw new Response("Not Found", { status: 404 });

  // #region State
  const [messagesPanelOpenState, setMessagesPanelOpenState] = useState({
    docked: true,
    overlay: false,
  });
  const [isEnding, setIsEnding] = useState(false);
  const [isRevising, setIsRevising] = useState(false);
  const [isLeavingTeam, setIsLeavingTeam] = useState(false);
  // #endregion

  // #region Global Variables
  const workAgreementId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const activeTab = useMemo(() => {
    const tab = pageTabParam as PageTab | undefined;
    return tab ?? PageTab.Details;
  }, [pageTabParam]);

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  // #region Work Agreement Queries
  const workAgreementJson = useSelector(getWorkAgreementById(workAgreementId));
  const workAgreement = useMemo(() => {
    if (!workAgreementJson || isWorkAgreementLoadIssue(workAgreementJson)) return null;
    return WorkAgreement.fromJSON(workAgreementJson);
  }, [workAgreementJson]);

  const workAgreementQuery = useGetWorkAgreementByIdQuery(
    !workAgreementId || workAgreementId.isEmpty
      ? skipToken
      : { workAgreementId: workAgreementId?.value ?? Guid.EMPTY.value, session },
  );

  // #region Work Agreement Mutations
  const [endWorkAgreement] = useEndWorkAgreementMutation();
  const [reviseWorkAgreement] = useReviseWorkAgreementMutation();
  const [quitWorkTeam] = useQuitWorkTeamMutation();

  // #region Messages Queries
  // Not this agreement's id: a revision continues the conversation of the agreement it replaced, so
  // both file their forums under the first agreement in the chain and either page opens the same
  // three rooms. It falls back to this agreement's id, which is what the server sends for one that
  // began its own chain.
  const conversationId = workAgreement?.conversationId ?? workAgreementId;
  const topic = useMemo(() => {
    return Topic.create({
      entityClass: new ForumEntityClass("Work.Agreement"),
      entityId: conversationId,
    });
  }, [conversationId]);
  const forums = useSelector(getForumsByTopic(topic));
  const sortedForums = useMemo(() => {
    return [...(forums ?? [])].sort((left, right) => {
      if (left.name.value === "All Stakeholders") return -1;
      if (right.name.value === "All Stakeholders") return 1;
      return 0;
    });
  }, [forums]);

  // #region Global State
  const isLoading = workAgreementQuery.isLoading || sessionPermissionsLoading;
  const isArchived = useMemo(() => {
    return location.pathname.includes("/archived/");
  }, [location.pathname]);

  /**
   * Whether the signed-in person leads one of this agreement's two teams.
   *
   * Both leaders hold the same authority over the agreement as a whole: `work/{id}/revise` and
   * `work/{id}/end-agreement` each admit `WorkAgreement.Client` and `WorkAgreement.Vendor`, which
   * are the two teams' leaders. The masking check is not ceremony — a counterparty's team arrives
   * as `Masked` when the reader may not see it, and `leader` does not exist on that value.
   */
  const isClientTeamLeader =
    MaskedTeam.isPresentNotMasked(workAgreement?.clientTeam) &&
    Boolean(workAgreement.clientTeam.leader?.userId.isEqualTo(session.user?.id));
  const isVendorTeamLeader =
    MaskedTeam.isPresentNotMasked(workAgreement?.vendorTeam) &&
    Boolean(workAgreement.vendorTeam.leader?.userId.isEqualTo(session.user?.id));

  // #region Lifecycle
  useEffect(() => {
    // Work Agreement invalid/not found
    const shouldShowNotFound =
      Boolean(workAgreementId) && !workAgreementId?.isEmpty && workAgreement === null && !workAgreementQuery.isLoading;

    if (shouldShowNotFound && idParam) {
      enqueueSnackbar("Work Agreement not found", { variant: "warning" });
      navigate(`/work/${isArchived ? "archived" : "active"}`, { replace: true });
    }
  }, [activeTab, idParam, navigate, workAgreement, workAgreementId, workAgreementQuery.isLoading, isArchived]);

  useEffect(() => {
    if (!workAgreement?.id) return;
    const shouldUseArchivedRoute = Boolean(workAgreement.archiveDate);
    if (shouldUseArchivedRoute === isArchived) return;

    navigate(
      `/work/${shouldUseArchivedRoute ? "archived" : "active"}/view/${workAgreement.id.value}/${activeTab}`,
      { replace: true },
    );
  }, [activeTab, isArchived, navigate, workAgreement?.archiveDate, workAgreement?.id]);

  // Permissions bootstrap
  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);
  // #endregion

  // #region Functions
  const messagesPanelIsOpen = isMessageDrawerOverlay ? messagesPanelOpenState.overlay : messagesPanelOpenState.docked;

  function handleToggleMessagesPanel() {
    setMessagesPanelOpenState((prev) => {
      const panelMode = isMessageDrawerOverlay ? "overlay" : "docked";
      return {
        ...prev,
        [panelMode]: !prev[panelMode],
      };
    });
  }

  function handleCloseMessagesPanel() {
    setMessagesPanelOpenState((prev) => {
      const panelMode = isMessageDrawerOverlay ? "overlay" : "docked";
      return {
        ...prev,
        [panelMode]: false,
      };
    });
  }

  // Message threads bootstrap
  useEffect(() => {
    if (!workAgreement || workAgreementQuery.isLoading) return;
    dispatch(populateForumsByTopic({ session, topic }));
  }, [dispatch, session, topic, workAgreement, workAgreementId, workAgreementQuery.isLoading]);

  // Account view switching
  useEffect(() => {
    if (!workAgreement) return;
    if (
      MaskedTeam.isPresentNotMasked(workAgreement.vendorTeam) &&
      MaskedTeam.isPresentNotMasked(workAgreement.clientTeam) &&
      session.context?.viewingAsVendor &&
      workAgreement.clientTeam.leader?.userId.isEqualTo(session.user?.id)
    ) {
      session.setAccountViewType(AccountType.Client);
    }

    // If viewing as client but user is vendor team member or leader, switch to vendor view
    else if (
      MaskedTeam.isPresentNotMasked(workAgreement.vendorTeam) &&
      !session.context?.viewingAsVendor &&
      (workAgreement.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
        workAgreement.vendorTeam?.members?.some((member) => member.userId.isEqualTo(session.user?.id)))
    ) {
      session.setAccountViewType(AccountType.Vendor);
    }
  }, [workAgreement, workAgreement?.id?.value, session]);

  function handleTabChange(_event: ChangeEvent<object> | null, newTab: PageTab) {
    navigate(`/work/${isArchived ? "archived" : "active"}/view/${workAgreementId?.value}/${newTab}`);
  }

  const handleNavigateBack = useCallback(() => {
    navigate(`/work/${isArchived ? "archived" : "active"}`, { replace: true });
  }, [navigate, isArchived]);

  async function handleEndWorkAgreement() {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }

    const response = await confirm({
      title: "End Work Agreement?",
      message:
        "This will void the work agreement for any future work and only work performed " +
        "before this date will be billable until a new proposal is agreed to.",
      okButtonText: "End",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsEnding(true);

      await endWorkAgreement({ workAgreementId: workAgreement.id.value }).unwrap();
      enqueueSnackbar("Work agreement ended", { variant: "success" });
      navigate(`/work/archived/view/${workAgreement.id.value}/details`, { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to end work agreement", { variant: "error" });
    } finally {
      setIsEnding(false);
    }
  }

  async function handleReviseWorkAgreement() {
    if (!workAgreement?.id) {
      throw new Error("Work agreement not found");
    }
    if (!session.user) {
      throw new Error("User not found");
    }

    const response = await confirm({
      title: "Revise Work Agreement?",
      message:
        "When these changes are accepted and hired, this work agreement will end at the " +
        "start date of the new agreement.",
      okButtonText: "Revise",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsRevising(true);
      const proposalJson = await reviseWorkAgreement({ workAgreement, session }).unwrap();
      if (!proposalJson.id) {
        throw new Error("Invalid proposal response");
      }
      enqueueSnackbar("New revision created", { variant: "success" });
      navigate(`/proposals/active/revise/${proposalJson.id}/details`, { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(getErrorMessageFromUnknown(error) ?? "Failed to revise work agreement", { variant: "error" });
    } finally {
      setIsRevising(false);
    }
  }

  async function handleLeaveTeam() {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }
    if (!session.user?.id) {
      console.error("User not found");
      return;
    }

    const response = await confirm({
      title: "Leave Team?",
      message: "Are you sure you want to leave the vendor-team?",
      okButtonText: "Leave",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsLeavingTeam(true);
      await quitWorkTeam({ workId: workAgreement.id.value }).unwrap();
      enqueueSnackbar("You have left the vendor-team", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to leave vendor-team", { variant: "error" });
    } finally {
      setIsLeavingTeam(false);
    }
  }

  // #region Render
  if (!sessionPermissions || isLoading)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToWork) return <NotAuthorized />;

  return (
    // The measured width is published for the tabs, and the scaled type for everything below —
    // including the chat drawer, which is a sibling of the pane rather than a child of it and so
    // takes the theme but not the container. See `work-pane-typography`.
    <WorkPaneWidthContext.Provider value={paneWidth}>
    <ThemeProvider theme={paneTheme}>
    <PageContainer>
      <Main
        ref={paneRef}
        open={messagesPanelIsOpen}
        isMessageDrawerOverlay={isMessageDrawerOverlay}
        showMessageDrawer={!!workAgreement}
      >
        <StickyPageHeader>
          <HeaderContainer>
            <TitleContainer>
            {paneIsNarrow ? (
              <IconButton 
                onClick={handleNavigateBack} 
                color="primary" 
                edge="start" 
                size="medium" 
              >
                <ArrowBackIcon fontSize="large" />
              </IconButton>
            ) : (
              <Chip
                icon={<ArrowBackIcon />}
                size="medium"
                label="Back"
                onClick={handleNavigateBack}
                color="primary"
                sx={{ mr: 2 }}
              />
            )}
              <PageTitle variant="h4">
                <PageTitleBreadcrumb>Work Agreements &gt;</PageTitleBreadcrumb>
                {workAgreement?.name?.value ?? "Loading..."}
              </PageTitle>
            </TitleContainer>
            <ButtonContainer>
              <MessageButton
                size="medium"
                color="primary"
                disabled={!workAgreement?.id || !workAgreement}
                onClick={() => handleToggleMessagesPanel()}
              >
                <MessageIcon fontSize="medium" />
              </MessageButton>
              <MessageButtonLabel
                variant="button"
                color={!workAgreement?.id || !workAgreement ? "darkgray" : "primary"}
              >
                Messages
              </MessageButtonLabel>
            </ButtonContainer>
          </HeaderContainer>
          <Header>
            <TabsContainer
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              textColor="primary"
              allowScrollButtonsMobile
              value={activeTab}
              onChange={handleTabChange}
            >
              <Tab value={PageTab.Details} label={WorkAgreementFieldCategory.Details} />
              <Tab value={PageTab.ClientTeam} label="Client Team" />
              <Tab value={PageTab.Team} label="Vendor Team" />
              <Tab value={PageTab.FeeSchedule} label={WorkAgreementFieldCategory.FeeSchedule} />
              <Tab value={PageTab.Conflicts} label={WorkAgreementFieldCategory.Conflicts} />
              <Tab value={PageTab.Policies} label={WorkAgreementFieldCategory.Policies} />
              <Tab value={PageTab.Discount} label={WorkAgreementFieldCategory.Discount} />
            </TabsContainer>
          </Header>
        </StickyPageHeader>
        {/*
          * Above the action row, not in it. The server decides who is told about an open revision
          * -- an unsubmitted draft is private to the party that opened it -- so the id arriving at
          * all is the permission to show this, exactly as it is for the link to the same proposal
          * on the Details tab. Withheld from an archived agreement, where the sentence would not be
          * true: an agreement that has ended does not run on until anything is hired.
          */}
        {!isArchived && workAgreement?.openRevisionProposalId && (
          <ReviewRequestedBanner
            severity="info"
            action={
              <Button
                color="inherit"
                component={Link}
                size="small"
                to={`/proposals/active/revise/${workAgreement.openRevisionProposalId.value}/details`}
              >
                Review Revision
              </Button>
            }
          >
            A revision of this agreement is being negotiated. This agreement runs unchanged until
            that revision is hired.
          </ReviewRequestedBanner>
        )}
        {!isArchived && (
          <ActionsContainer>
            <WorkAgreementActions>
              {(isClientTeamLeader || isVendorTeamLeader) && (
                <Tooltip
                  enterDelay={600}
                  placement="bottom-start"
                  title="Revise the work agreement to make changes to terms or team members. 
                  This will end the current agreement and create a new proposal for review."
                >
                  <WorkAgreementActionSpan>
                    <WorkAgreementActionButton
                      variant="outlined"
                      color="primary"
                      startIcon={<EditSquareIcon />}
                      loading={isRevising}
                      disabled={isEnding || isRevising || isLeavingTeam}
                      onClick={handleReviseWorkAgreement}
                    >
                      Revise
                    </WorkAgreementActionButton>
                  </WorkAgreementActionSpan>
                </Tooltip>
              )}
              {MaskedTeam.isPresentNotMasked(workAgreement?.vendorTeam) &&
                workAgreement?.vendorTeam?.members.some((member) => member.userId.isEqualTo(session.user?.id)) && (
                  <Tooltip
                    title="Leave the vendor team. All work done after leaving will not be tracked."
                    placement="bottom"
                  >
                    <LeaveTeamActionButton
                      variant="outlined"
                      color="error"
                      startIcon={<LogoutIcon />}
                      loading={isLeavingTeam}
                      disabled={isEnding || isRevising || isLeavingTeam}
                      onClick={handleLeaveTeam}
                    >
                      Leave Team
                    </LeaveTeamActionButton>
                  </Tooltip>
                )}
              {(isClientTeamLeader || isVendorTeamLeader) && (
                <Tooltip title="End Work Agreement" placement="bottom">
                  <EndAgreementActionSpan>
                    <EndAgreementActionButton
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      loading={isEnding}
                      disabled={isEnding || isRevising || isLeavingTeam}
                      onClick={handleEndWorkAgreement}
                    >
                      End Agreement
                    </EndAgreementActionButton>
                  </EndAgreementActionSpan>
                </Tooltip>
              )}
            </WorkAgreementActions>
          </ActionsContainer>
        )}
        {/* Flush to the pane's edges: a tab is panels and tables that already carry padding of their
            own, and they are meant to meet the navigation drawer on one side and the chat on the
            other. The prose inside them asks for its own inset — `workPaneProseInset`. */}
        <Content {...paneFlushContent}>
          <TabContent>
            {workAgreement && (
              <>
                {activeTab === PageTab.Details && <DetailsTab workAgreement={workAgreement} />}
                {activeTab === PageTab.ClientTeam && <ClientTeamTab workAgreement={workAgreement} />}
                {activeTab === PageTab.Team && (
                  <TeamTab entityId={session.user!.legalEntityId} workAgreement={workAgreement} />
                )}
                {activeTab === PageTab.FeeSchedule && <FeeScheduleTab workAgreement={workAgreement} />}
                {activeTab === PageTab.Conflicts && <ConflictsTab workAgreement={workAgreement} />}
                {activeTab === PageTab.Policies && <PoliciesTab workAgreement={workAgreement} />}
                {activeTab === PageTab.Discount && <DiscountTab workAgreement={workAgreement} />}
              </>
            )}
          </TabContent>
        </Content>
      </Main>
      <ChatDrawer
        forums={sortedForums}
        isOverlay={isMessageDrawerOverlay}
        isOpen={messagesPanelIsOpen}
        readOnly={isArchived}
        onClose={handleCloseMessagesPanel}
      />
    </PageContainer>
    </ThemeProvider>
    </WorkPaneWidthContext.Provider>
  );
}
